let cached = null;
let inflight = null;

/**
 * Fetch validation suggestions derived from form-templates MCP guidelines.
 * Returns byType (patterns, validation, suggestedErrorMessage per field type)
 * and commonErrorMessages (quick-pick list for validation error message).
 * @returns {Promise<{ byType: Object, commonErrorMessages: string[] }>}
 */
export const getValidationSuggestions = async () => {
    if (cached) return cached;
    if (!inflight) {
        inflight = fetch(`${process.env.PUBLIC_URL || ""}/validation-suggestions.json`)
            .then((r) => (r.ok ? r.json() : { byType: {}, commonErrorMessages: [] }))
            .then((data) => {
                cached = data;
                return cached;
            })
            .catch(() => {
                cached = { byType: {}, commonErrorMessages: [] };
                return cached;
            });
    }
    return inflight;
};

/**
 * Get validation quick recommendations for a field type (patterns, validation min/max, suggested error message, placeholder).
 * @param {string} fieldType - e.g. "currency", "datetime", "email", "input", "percentage", "isTime"
 * @returns {Promise<{ patterns: string[], validation: { min?: number, max?: number }, suggestedErrorMessage: string, suggestedPlaceholder?: string }>}
 */
export const getValidationSuggestionsForType = async (fieldType) => {
    const { byType } = await getValidationSuggestions();
    const typeKey = fieldType && typeof fieldType === "string" ? fieldType.trim() : "input";
    const entry = byType[typeKey] || byType.input || {};
    return {
        patterns: Array.isArray(entry.patterns) ? entry.patterns : [],
        validation: entry.validation && typeof entry.validation === "object" ? entry.validation : {},
        suggestedErrorMessage: entry.suggestedErrorMessage || "Required",
        suggestedPlaceholder: entry.suggestedPlaceholder != null ? entry.suggestedPlaceholder : undefined,
    };
};

/**
 * Get common validation error messages for quick pick (e.g. Autocomplete options).
 * @returns {Promise<string[]>}
 */
export const getValidationErrorMessageSuggestions = async () => {
    const { commonErrorMessages } = await getValidationSuggestions();
    return Array.isArray(commonErrorMessages) ? commonErrorMessages : [];
};

/**
 * Get all validation patterns used in form templates (from MCP-derived data).
 * @returns {Promise<string[]>}
 */
export const getUsedValidationPatterns = async () => {
    const data = await getValidationSuggestions();
    const list = data.usedPatternsFromTemplates;
    return Array.isArray(list) ? list : [];
};

/**
 * Get validation patterns used in form templates for a given format (e.g. "currency", "percentage").
 * @param {string} format - metadata.format value, e.g. "currency" or "percentage"
 * @returns {Promise<string[]>}
 */
export const getUsedValidationPatternsByFormat = async (format) => {
    const data = await getValidationSuggestions();
    const byFormat = data.usedPatternsByFormat;
    if (!byFormat || typeof byFormat !== "object") return [];
    const list = byFormat[format];
    return Array.isArray(list) ? list : [];
};
